import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileSignature, Plus, Send, Eye, Trash2, ShieldX, Loader2, Mail, CheckCircle2, MailOpen, MousePointerClick, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { contractTemplatesAPI, contractsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ContractDocument from './ContractDocument';
import { ReissueLetterButton, ReissueNote } from './ReissueLetterButton';

const STATUS_BADGE = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  viewed: 'bg-amber-100 text-amber-700',
  signed: 'bg-emerald-100 text-emerald-700',
  voided: 'bg-red-100 text-red-700',
};

const MODE_CONFIG = {
  establishment: {
    panelHeading: 'Experiential Training Agreements',
    sendLabel: 'Send',
    emptyHint: 'No active Establishment templates. Visit Contract Templates and seed the default.',
    listEmpty: 'No contracts yet for this restaurant.',
    sendVerb: 'Send',
  },
  placement: {
    panelHeading: 'Student Placement Confirmation Letters',
    sendLabel: 'Send to student',
    emptyHint: 'No active Placement templates. Visit Contract Templates and seed defaults.',
    listEmpty: 'No student placement letters yet.',
    sendVerb: 'Send to student',
  },
};

/**
 * Generic contracts management panel.
 * Props:
 *  - mode: 'establishment' | 'placement'
 *  - subjectId: restaurant_id (establishment mode) | placement_id (placement mode)
 *  - testIdScope: prefix for data-testids (defaults: 'contract' | 'placement-contract')
 *
 * Lists existing contracts (slim summary), supports create/send/void/delete/view + copy-link.
 */
const ContractsPanel = ({ mode = 'establishment', subjectId, testIdScope }) => {
  const cfg = MODE_CONFIG[mode];
  const scope = testIdScope || (mode === 'placement' ? 'placement-contract' : 'contract');
  const { isReadOnly } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTemplateId, setNewTemplateId] = useState('');
  const [viewing, setViewing] = useState(null);
  const [viewingFull, setViewingFull] = useState(null);

  const fetchContracts = () =>
    mode === 'placement'
      ? contractsAPI.listForPlacement(subjectId)
      : contractsAPI.listForRestaurant(subjectId);

  const reload = async () => {
    setLoading(true);
    try {
      const [cRes, tRes] = await Promise.all([
        fetchContracts(),
        contractTemplatesAPI.list({ category: mode }),
      ]);
      setContracts(cRes.data || []);
      const active = (tRes.data || []).filter((t) => t.active);
      setTemplates(active);
      if (active.length && !newTemplateId) setNewTemplateId(active[0].id);
    } catch {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (subjectId) reload(); /* eslint-disable-next-line */ }, [subjectId, mode]);

  const handleCreate = async () => {
    if (!newTemplateId) { toast.error('Choose a template'); return; }
    setCreating(true);
    try {
      const payload = mode === 'placement'
        ? { template_id: newTemplateId, placement_id: subjectId }
        : { template_id: newTemplateId, restaurant_id: subjectId };
      await contractsAPI.create(payload);
      toast.success('Contract created (draft)');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const handleSend = async (c) => {
    try {
      const res = await contractsAPI.send(c.id, { base_url: window.location.origin });
      const cc = res.data?.cc || [];
      toast.success(cc.length ? `Sent to ${res.data?.recipient} (cc: ${cc.join(', ')})` : `Sent to ${res.data?.recipient}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
  };

  const handleRefresh = async (c) => {
    try {
      await contractsAPI.refresh(c.id);
      toast.success('Letter re-synced with the cycle phases');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    }
  };
  const handleVoid = async (c) => {
    if (!window.confirm('Void this contract? It will no longer be signable.')) return;
    try {
      await contractsAPI.void(c.id);
      toast.success('Contract voided');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Void failed');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm('Delete this draft permanently?')) return;
    try {
      await contractsAPI.delete(c.id);
      toast.success('Deleted');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const openView = async (c) => {
    setViewing(c);
    setViewingFull(null);
    try {
      const res = await contractsAPI.get(c.id);
      setViewingFull(res.data);
    } catch {
      toast.error('Failed to load contract details');
    }
  };

  const copyLink = async (c) => {
    const url = `${window.location.origin}/contract/sign/${c.access_token}`;
    try { await navigator.clipboard.writeText(url); toast.success('Signing link copied'); }
    catch { toast.error('Copy failed'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-ica-navy" />
      </div>
    );
  }

  return (
    <div data-testid={`${scope}-panel`} className="space-y-3">
      <div className="flex items-center gap-2 pt-1">
        <FileSignature className="w-4 h-4 text-ica-navy" />
        <h3 className="font-semibold text-sm text-ica-navy">{cfg.panelHeading}</h3>
      </div>

      {templates.length === 0 ? (
        <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded p-2">{cfg.emptyHint}</p>
      ) : isReadOnly ? null : (
        <div className="flex gap-2">
          <Select value={newTemplateId} onValueChange={setNewTemplateId}>
            <SelectTrigger data-testid={`${scope}-template-select`} className="flex-1">
              <SelectValue placeholder="Pick a template" />
            </SelectTrigger>
            <SelectContent>
              {templates.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {mode === 'placement' ? t.name : `${t.name} · ${t.year_of_study}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid={`create-${scope}-btn`}
            type="button"
            size="sm"
            onClick={handleCreate}
            disabled={creating}
            
          >
            <Plus className="w-4 h-4 mr-1" /> Create
          </Button>
        </div>
      )}

      {contracts.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-3">{cfg.listEmpty}</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {contracts.map((c) => (
            <div
              key={c.id}
              data-testid={`${scope}-row-${c.id}`}
              className="border rounded p-2.5 bg-white"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{c.template_name || 'Contract'}</p>
                  <p className="text-[11px] text-gray-500 truncate">
                    {mode === 'establishment' && c.year_of_study ? `${c.year_of_study} · ` : ''}
                    Created {new Date(c.created_at).toLocaleDateString()}
                    {c.signed_at && <> · signed by <strong>{c.signer_name}</strong></>}
                  </p>
                  {c.last_sent_to && (
                    <p
                      data-testid={`${scope}-recipient-${c.id}`}
                      className="text-[11px] text-gray-600 truncate mt-0.5 flex items-center gap-1"
                    >
                      <Mail className="w-3 h-3 text-gray-400 shrink-0" />
                      Sent to <span className="font-medium text-gray-800">{c.last_sent_to}</span>
                      {c.sent_at && <> on {new Date(c.sent_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</>}
                      {c.last_sent_cc?.length > 0 && (
                        <span className="text-gray-400 truncate" title={`cc: ${c.last_sent_cc.join(', ')}`}>
                          · cc {c.last_sent_cc.length}
                        </span>
                      )}
                    </p>
                  )}
                  {(c.email_delivered_at || c.email_opened_at || c.email_clicked_at) && (
                    <div className="flex items-center gap-2 mt-0.5 text-[10px] text-gray-500">
                      {c.email_delivered_at && (
                        <span data-testid={`${scope}-delivered-${c.id}`} className="inline-flex items-center gap-0.5 text-emerald-700">
                          <CheckCircle2 className="w-2.5 h-2.5" /> delivered
                        </span>
                      )}
                      {c.email_opened_at && (
                        <span data-testid={`${scope}-opened-${c.id}`} className="inline-flex items-center gap-0.5 text-sky-700">
                          <MailOpen className="w-2.5 h-2.5" /> opened
                        </span>
                      )}
                      {c.email_clicked_at && (
                        <span data-testid={`${scope}-clicked-${c.id}`} className="inline-flex items-center gap-0.5 text-indigo-700">
                          <MousePointerClick className="w-2.5 h-2.5" /> clicked
                        </span>
                      )}
                    </div>
                  )}
                  <ReissueNote contract={c} />
                </div>
                <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${STATUS_BADGE[c.status] || ''}`}>
                  {c.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <Button data-testid={`view-${scope}-${c.id}`} type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => openView(c)}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                </Button>
                {!isReadOnly && c.status !== 'signed' && c.status !== 'voided' && (
                  <Button data-testid={`send-${scope}-${c.id}`} type="button" size="sm" variant="ghost" className="h-7 text-xs text-blue-700 hover:bg-blue-50" onClick={() => handleSend(c)}>
                    <Send className="w-3.5 h-3.5 mr-1" /> {c.status === 'draft' ? cfg.sendVerb : 'Resend'}
                  </Button>
                )}
                <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => copyLink(c)}>Copy link</Button>
                {!isReadOnly && mode === 'placement' && c.status !== 'signed' && c.status !== 'voided' && (
                  <Button data-testid={`refresh-${scope}-${c.id}`} type="button" size="sm" variant="ghost" className="h-7 text-xs text-gray-600" title="Re-sync with the cycle's current phases" onClick={() => handleRefresh(c)}>
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                  </Button>
                )}
                {!isReadOnly && c.status !== 'signed' && c.status !== 'voided' && (
                  <Button data-testid={`void-${scope}-${c.id}`} type="button" size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50 ml-auto" onClick={() => handleVoid(c)}>
                    <ShieldX className="w-3.5 h-3.5 mr-1" /> Void
                  </Button>
                )}
                {!isReadOnly && mode === 'placement' && (
                  <ReissueLetterButton contract={c} onDone={reload} className="ml-auto" />
                )}
                {!isReadOnly && c.status === 'draft' && (
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => handleDelete(c)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) { setViewing(null); setViewingFull(null); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewing?.template_name}</DialogTitle>
            <DialogDescription>
              {viewing?.status === 'signed'
                ? `Signed on ${new Date(viewing?.signed_at).toLocaleString()} by ${viewing?.signer_name}`
                : `Status: ${viewing?.status}`}
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3">
              {viewing.status !== 'signed' && (
                <a
                  href={`${window.location.origin}/contract/sign/${viewing.access_token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-sm text-ica-navy underline"
                >
                  Open public signing page →
                </a>
              )}
              {viewingFull ? (
                <ContractDocument
                  bodyHtml={viewingFull.template_snapshot?.body_html || ''}
                  fieldValues={viewingFull.field_values || {}}
                  signatures={{
                    signer: viewingFull.signer_signature_data,
                    witness: viewingFull.witness_signature_data,
                    ica_signer: viewingFull.ica_signature_data,
                  }}
                  logoUrl={viewingFull.logo_url}
                  senderBlockHtml={viewingFull.template_snapshot?.sender_block_html}
                />
              ) : (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-ica-navy" /></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setViewing(null); setViewingFull(null); }}>Close</Button>
            {viewing?.status === 'signed' && (
              <Button type="button" onClick={() => window.open(`/contract/sign/${viewing.access_token}`, '_blank')} >
                Open & Print
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractsPanel;
